import React, { useContext, useMemo, useState } from "react";
import Title from "../../../components/Title";
import BarGraph from "../../../components/BarGraph";
import { Detalle } from "../../../utils/constants";
import AppContext, { IFilters } from "../../../AppContext";
import { gql, useQuery } from "@apollo/client";
import {
  getStepsFixed as getSteps,
  getMinFixed as getMin,
  getMaxFixed as getMax,
} from "../../../utils/functions";
import { MessageBar, Spinner, SpinnerSize } from "@fluentui/react";
import LocalFilter from "../../../components/LocalFilter";
import { useTranslation } from "react-i18next";

export const titleEn = "Segment by channel";
export const title = "Segmentos por canal de servicio";
export const filters: IFilters = {
  limitedPeriodo: false,
  showMedidas: true,
  hasTicketMedio: false,
  isDesglose: true,
  showEntorno: false,
  showSegmentos: false,
  showMyData: false,
};

type ICanal =
  | { key: "0"; text: string } // Total
  | { key: "1"; text: string } // Delivery
  | { key: "2"; text: string } // Take-away
  | { key: "3"; text: string }; // On-premises

const canalBackendKey = (key: ICanal["key"]): "Total" | "Domicilio" | "Llevar" | "Local" => {
  switch (key) {
    case "1":
      return "Domicilio";
    case "2":
      return "Llevar";
    case "3":
      return "Local";
    case "0":
    default:
      return "Total";
  }
};


const translateSimpleLabel = (raw: string, t: (k: string) => string) => {
  if (!raw || typeof raw !== "string") return raw;
  const norm = raw.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (norm === "mercado") return t("Market") as string;
  if (norm === "cafe") return t("Cafe") as string;
  return raw;
};


function translateExportKeys(input: any, t: (k: string) => string): any {
  const translateKey = (raw: string): string => {
    if (!raw || typeof raw !== "string") return raw as any;
    
    let s = raw.replace(/_/g, " ").trim().toLowerCase();

 
    const map: Record<string, string> = {
      mercado: t("Market"),
      market: t("Market"),
      cafe: t("Cafe"),
      coffee: t("Cafe"),
      ventas: t("Sales"),
      tickets: t("Tickets"),
      "ticket medio": t("Average-ticket"),
      totales: t("Totals"),
      total: t("Total"),
      comparables: t("Comparables"),
      comparable: t("Comparables"),
    
      sales: t("Sales"),
      "average ticket": t("Average-ticket"),
      scope: t("Scope"),
      measures: t("Measures"),
      variable: t("Variable"),
      subvariable: t("Subvariable"),
      channel: t("Channel"),
      
      domicilio: t("Delivery"),
      llevar: t("Take-away"),
      local: t("On-premises"),
    };

    
    s = s
      .replace(/\bmercado\b/gi, map["mercado"])
      .replace(/\bmarket\b/gi, map["market"])
      .replace(/\bcaf[eé]\b/gi, map["cafe"])
      .replace(/\bcoffee\b/gi, map["coffee"])
      .replace(/\bventas\b/gi, map["ventas"])
      .replace(/\btickets\b/gi, map["tickets"])
      .replace(/\bticket\s*medio\b/gi, map["ticket medio"])
      .replace(/\btotales\b/gi, map["totales"])
      .replace(/\btotal\b/gi, map["total"])
      .replace(/\bcomparables?\b/gi, map["comparables"])
      .replace(/\bsales\b/gi, map["sales"])
      .replace(/\baverage\s*ticket\b/gi, map["average ticket"])
      .replace(/\bscope\b/gi, map["scope"])
      .replace(/\bmeasures\b/gi, map["measures"])
      .replace(/\bvariable\b/gi, map["variable"])
      .replace(/\bsubvariable\b/gi, map["subvariable"])
      .replace(/\bchannel\b/gi, map["channel"])
      .replace(/\bdomicilio\b/gi, map["domicilio"])
      .replace(/\bllevar\b/gi, map["llevar"])
      .replace(/\blocal\b/gi, map["local"]);

   
    if (/^[a-z]/.test(s)) s = s.charAt(0).toUpperCase() + s.slice(1);
    return s;
  };

  if (Array.isArray(input)) {
    return input.map((item) => translateExportKeys(item, t));
  }
  if (input && typeof input === "object") {
    const out: any = {};
    Object.keys(input).forEach((k) => {
      const newKey = translateKey(k);
      out[newKey] = translateExportKeys(input[k], t);
    });
    return out;
  }
  return input;
}

const Graph = () => {
  const { t } = useTranslation();
  const { semanas, filters, user } = useContext(AppContext);
  const isDesglosado = filters.detalle === Detalle.Desglose;

  
  const [canal, setCanal] = useState<ICanal>({ key: "0", text: "Total" });

  const GET_GRAPH = gql`
    query ($yr1: Int!, $wk1: Int!, $yr2: Int!, $wk2: Int!) {
      graph(
        Graph: "Segmentos por canal de servicio"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
      )
    }
  `;

  const qry = useQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
    variables: {
      yr1: semanas[0].year,
      wk1: semanas[0].week,
      yr2: semanas[1].year,
      wk2: semanas[1].week,
    },
  });

  const EXPORT_GRAPH = gql`
    query ($yr1: Int!, $wk1: Int!, $yr2: Int!, $wk2: Int!, $var: Int) {
      graph(
        Graph: "Segmentos por canal de servicio export"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: { Variables: $var }
      )
    }
  `;

  const exportQry = useQuery(EXPORT_GRAPH, {
    variables: {
      yr1: semanas[0].year,
      wk1: semanas[0].week,
      yr2: semanas[1].year,
      wk2: semanas[1].week,
      var: 0, //filters.variables
    },
  });

  let errorMsg = t("An-error-has-occur-when-recovering-data") as string;
  let loading = qry.loading || qry.data === undefined;
  let error = !semanas || !!qry.error;

  let labels: string[] = [];
  let values_total: number[][] = useMemo(() => [], []);
  let values_desglose: number[][] = useMemo(() => [], []);
  let annotation: any[] = [];

  if (!loading && !error && qry.data?.graph) {
    try {
      const data = qry?.data?.graph;
      if (!data) throw new Error(errorMsg);
      if (data?.Error) throw new Error(`${data.Error}`);

      const backendKey = canalBackendKey(canal.key);

      labels = data.map((d: any) => Object.keys(d)[0]);

      values_total = [
        data.map(
          (d: any[]) => (Object.values(d)[0] as any)[backendKey].Ventas.Tendencia
        ),
      ];
      values_desglose = [
        data.map(
          (d: any[]) => (Object.values(d)[0] as any)[backendKey].Tickets.Tendencia
        ),
        data.map(
          (d: any[]) =>
            (Object.values(d)[0] as any)[backendKey].TicketMedio.Tendencia
        ),
      ];
    } catch (err: any) {
      error = true;
      errorMsg = err.message ?? errorMsg;
    }
  }

  let max: number | undefined = useMemo(
    () => getMax([...values_total, ...values_desglose]),
    [values_total, values_desglose]
  );
  let min: number | undefined = useMemo(
    () => getMin([...values_total, ...values_desglose]),
    [values_total, values_desglose]
  );
  
  max = Math.ceil(max ?? 0);
  min = Math.floor(min ?? 0);

  const stepSize = useMemo(() => getSteps(min, max), [min, max]);

 
  let exportData: any = [];
  const exportError = !semanas || !!exportQry.error;
  const exportLoading = exportQry.loading || exportQry.data === undefined;

  if (!exportLoading && !exportError && exportQry.data?.graph) {
    try {
      const tempData = exportQry?.data?.graph;
      if (tempData && tempData[0]) {
        const translatedArray: any[] = [];
        Object.keys(tempData[0]).forEach((key, i) => {
       
          const obj = { [key]: Object.values(tempData[0])[i] };
          translatedArray.push(translateExportKeys(obj, (k) => t(k) as string));
        });
        exportData = translatedArray;
      }
    } catch (err: any) {
      console.log(err.message);
    }
  }

  const values = isDesglosado ? values_desglose : values_total;

  const graphData = {
    datalabels: isDesglosado ? [t("Tickets"), t("Average-ticket")] : [t("Sales")],
    
    labels: labels.map((lab) => translateSimpleLabel(lab, t)),
    values: values,
    colors: isDesglosado ? ["#0C4D7B", "#85b709"] : ["#0e6773"],
    annotation: annotation,
    series: isDesglosado
      ? [
          { name: t("Tickets"), data: values[0] ?? [] },
          { name: t("Average-ticket"), data: values[1] ?? [] },
        ]
      : [{ name: t("Sales"), data: values[0] }],
    min: min,
    max: max,
    stepSize: stepSize,
  };

  return (
    <>
      <Title
        title={t("Segments-by-channel")}
        error={!!error}
        files={{
          data: exportData,
          semanas,
          user,
          title: t("Segments-by-channel") as string,
          leyenda: t("Measures") as string,
          transposed: true,
        }}
      />
      {error && <MessageBar isMultiline>{errorMsg}</MessageBar>}
      {loading && (
        <div className="loading-graph">
          <Spinner size={SpinnerSize.large} />
        </div>
      )}
      {!error && (
        <>
          <LocalFilter
            title={t("Channel")}
            fulltitle={t("Service-Channel")}
            values={["Total", t("Delivery"), t("Take-away"), t("On-premises")]}
            selected={{
              key: canal.key,
              text:
                canal.key === "0"
                  ? "Total"
                  : canal.key === "1"
                  ? t("Delivery")
                  : canal.key === "2"
                  ? t("Take-away")
                  : t("On-premises"),
            }}
            setSelected={setCanal}
          />
          <BarGraph data={graphData} percentages />
        </>
      )}
    </>
  );
};

export default Graph;